import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  StarIcon, 
  MapPinIcon, 
  AcademicCapIcon, 
  HeartIcon, 
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LanguageIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { Guide } from '../types';
import { guideService } from '../services/guideService';
import { useTheme } from '../contexts/ThemeContext';

const GuideCard: React.FC<{ guide: Guide }> = ({ guide }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { actualTheme } = useTheme();
  
  return (
    <motion.div
      layout
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`rounded-[2.5rem] border shadow-modern overflow-hidden flex flex-col h-fit h-auto transition-colors duration-500 ${
        actualTheme === 'dark' 
          ? 'bg-slate-900/60 border-slate-800 backdrop-blur-xl' 
          : 'bg-white border-slate-100'
      }`}
    >
      {/* Upper Section - Always Visible */}
      <div className="p-8 pb-10 flex flex-col items-center text-center">
        <div className="relative mb-8">
          <div className="w-40 h-40 rounded-[2.5rem] overflow-hidden shadow-inner border-[6px] border-slate-50 dark:border-slate-800/50 bg-neutral-100">
            {guide.avatar ? (
              <img src={guide.avatar} alt={guide.firstName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
                <UserIcon className="h-16 w-16 text-slate-300" />
              </div>
            )}
          </div>
          <div className="absolute -bottom-3 -right-3 bg-blue-600 text-white p-2.5 rounded-2xl shadow-xl animate-bounce-slow">
             <StarIcon className="h-5 w-5 fill-white" />
          </div>
        </div>
        
        <h3 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-3">
          {guide.firstName} {guide.lastName}
        </h3>
        
        <div className="flex items-center gap-1.5 mb-6 px-4 py-1.5 bg-blue-50/50 dark:bg-blue-900/10 rounded-full border border-blue-100/50 dark:border-blue-800/20">
          <span className="text-blue-600 dark:text-blue-400 font-black text-[10px] tracking-[0.2em] uppercase">Professional Guide</span>
          <span className="text-slate-300 dark:text-slate-600">•</span>
          <div className="flex items-center gap-1">
            <span className="text-slate-900 dark:text-white font-black text-xs">{guide.rating}</span>
            <span className="text-[10px] text-slate-400 font-bold">/ 5.0</span>
          </div>
        </div>
        
        <p className="text-slate-500 dark:text-slate-400 text-sm font-light leading-relaxed max-w-[240px] mb-8">
          Specializing in {guide.specialties.join(' & ')}
        </p>
        
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className={`group flex items-center gap-3 px-8 py-3 rounded-full text-xs font-bold transition-all duration-300 active:scale-95 border ${
            isExpanded 
              ? 'bg-red-50 text-red-600 border-red-100' 
              : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white shadow-lg'
          }`}
        >
          {isExpanded ? 'Close Profile' : 'View Full Profile'}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.5 }}
          >
            {isExpanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4 group-hover:translate-y-0.5 transition-transform" />}
          </motion.div>
        </button>
      </div>

      {/* Expandable Section */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden bg-slate-50/30 dark:bg-slate-900/20 border-t border-slate-100 dark:border-slate-800"
          >
            <div className="p-8 sm:p-10 space-y-10">
              {/* Bio Section */}
              <div className="relative">
                <div className="absolute -top-4 -left-4 text-6xl text-blue-600/5 font-serif">"</div>
                <p className="text-[11px] font-black text-blue-600 uppercase tracking-[0.3em] mb-4">The Safari Story</p>
                <p className="text-[15px] text-slate-600 dark:text-slate-300 font-light leading-loose italic relative z-10">
                  {guide.bio}
                </p>
              </div>

              {/* Technical Stats */}
              <div className="grid grid-cols-2 gap-6">
                <div className="p-6 rounded-[2rem] bg-white dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/30 shadow-sm">
                  <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center mb-4">
                    <AcademicCapIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Years Expertise</p>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{guide.experience}</p>
                </div>
                
                <div className="p-6 rounded-[2rem] bg-white dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/30 shadow-sm">
                  <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center mb-4">
                    <MapPinIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Primary Hub</p>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">{guide.location}</p>
                </div>
              </div>

              {/* Languages & Communications */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-2">
                   <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-900/20">
                    <LanguageIcon className="h-4 w-4 text-blue-600" />
                   </div>
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Communications</p>
                </div>
                <div className="flex flex-wrap gap-2 pl-11">
                  {guide.languages.map(lang => (
                    <span key={lang} className="text-xs font-bold px-4 py-1.5 rounded-xl bg-white dark:bg-slate-800 shadow-sm border border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-300">
                      {lang}
                    </span>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div className="pt-8 border-t border-slate-100 dark:border-slate-800">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Verified Safari Credentials</p>
                <div className="grid grid-cols-1 gap-3">
                   {guide.certifications.map(cert => (
                     <div key={cert} className="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-slate-800/30 border border-slate-50 dark:border-slate-700/20">
                        <CheckBadgeIcon className="h-5 w-5 text-blue-600 shrink-0" />
                        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{cert}</span>
                     </div>
                   ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Guides: React.FC = () => {
  const { actualTheme } = useTheme();
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let handleGuidesUpdate: (() => Promise<void>) | null = null;
    let refreshTimeout: NodeJS.Timeout | null = null;

    const loadGuides = async () => {
      try {
        const allGuides = await guideService.getGuides(false);
        console.log(`🔍 Guides component: Received ${allGuides.length} total guides from service`);
        
        const activeGuides = allGuides.filter(guide => guide.active);
        console.log(`🔍 Guides component: ${activeGuides.length} guides are active`);

        if (allGuides.length > 0 && activeGuides.length === 0) {
          console.warn('⚠️ All guides found are INACTIVE. Toggle them to active in Admin to see them here.');
        }

        setGuides(activeGuides);
        setLoading(false);

        if (allGuides.length === 0) {
          handleGuidesUpdate = async () => {
            const updatedGuides = await guideService.getGuides(false);
            const activeUpdated = updatedGuides.filter(guide => guide.active);
            if (activeUpdated.length > 0) setGuides(activeUpdated);
          };
          window.addEventListener('guidesUpdated', handleGuidesUpdate);
          refreshTimeout = setTimeout(async () => {
            const refreshedGuides = await guideService.getActiveGuides();
            if (refreshedGuides.length > 0) setGuides(refreshedGuides);
          }, 2000);
        } else {
          guideService.getActiveGuides().then(refreshedGuides => {
             setGuides(refreshedGuides);
          }).catch(() => {});
        }
      } catch (error) {
        console.error('Error loading guides:', error);
        setLoading(false);
      }
    };

    loadGuides();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && (e.key.startsWith('guides_') || e.key === 'guides_cache')) loadGuides();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (handleGuidesUpdate) window.removeEventListener('guidesUpdated', handleGuidesUpdate);
      if (refreshTimeout) clearTimeout(refreshTimeout);
    };
  }, []);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${actualTheme === 'dark' ? 'bg-slate-950' : 'bg-neutral-50'}`}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Summoning our expert team...</p>
        </div>
      </div>
    );
  }

  if (guides.length === 0) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center p-6 text-center ${actualTheme === 'dark' ? 'bg-slate-950' : 'bg-neutral-50'}`}>
        <div className="max-w-md w-full p-12 bg-white dark:bg-slate-900 rounded-[3rem] shadow-modern border border-slate-100 dark:border-slate-800">
          <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-8">
            <UserIcon className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-4 tracking-tight">Our Guides <br /> are Preparing.</h2>
          <p className="text-slate-500 font-light leading-relaxed mb-8">
            Our elite team of safari architects is currently being finalized. We only partner with the finest guides in East Africa.
          </p>
          <div className="pt-8 border-t border-slate-100 dark:border-slate-800 opacity-60">
             <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-2">Administrator Note</p>
             <p className="text-[10px] text-slate-400">Ensure guide profiles are toggled to 'Active' in the Admin Panel.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-32 ${actualTheme === 'dark' ? 'bg-slate-950' : 'bg-neutral-50'}`}>
      {/* Compact Floating Editorial Header */}
      <section className="pt-12 pb-8 sm:pt-20 sm:pb-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className={`relative z-10 p-8 sm:p-14 lg:p-20 rounded-[3rem] sm:rounded-[4.5rem] border shadow-modern text-center ${
              actualTheme === 'dark' 
                ? 'bg-slate-900/40 border-slate-800/50 backdrop-blur-xl' 
                : 'bg-white border-slate-100'
            }`}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mb-8"
            >
              <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30">
                <span className="text-blue-600 dark:text-blue-400 font-black uppercase tracking-[0.3em] text-[10px]">
                  Elite Craftsmanship
                </span>
              </div>
            </motion.div>
            
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tight text-slate-900 dark:text-white leading-tight mb-8">
              Our Expert <span className="font-['Playfair_Display'] italic text-blue-600 font-medium pb-2 relative">
                Guides.
                <span className="absolute bottom-0 left-0 w-full h-1 bg-blue-600/10 rounded-full" />
              </span>
            </h1>
            
            <p className="text-base sm:text-xl text-slate-500 dark:text-slate-400 max-w-2xl mx-auto font-light leading-relaxed font-['Outfit']">
              Meet the professional safari architects dedicated to crafting your perfect African adventure with unparalleled local expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Guides Interactive Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          <AnimatePresence mode="popLayout">
            {guides.map((guide) => (
              <GuideCard key={guide.id} guide={guide} />
            ))}
          </AnimatePresence>
        </div>
      </section>

      {/* Why Choose Section - Redesigned */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-40">
        <div className={`p-10 sm:p-20 rounded-[3rem] sm:rounded-[5rem] border ${
          actualTheme === 'dark' ? 'bg-slate-950 border-slate-800' : 'bg-white border-slate-100'
        }`}>
          <h2 className="text-3xl sm:text-5xl font-black text-slate-900 dark:text-white mb-16 text-center tracking-tight">
            The <span className="italic font-['Playfair_Display'] text-blue-600">SightTrails</span> Standard
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            <div className="text-center group">
              <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/10 rounded-3xl flex items-center justify-center mx-auto mb-8 transition-transform group-hover:rotate-6">
                <AcademicCapIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Master Expertise</h3>
              <p className="text-slate-500 dark:text-slate-400 font-light leading-relaxed">
                Extensive training in wildlife, conservation, and technical photography.
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/10 rounded-3xl flex items-center justify-center mx-auto mb-8 transition-transform group-hover:-rotate-6">
                <HeartIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Passionate Care</h3>
              <p className="text-slate-500 dark:text-slate-400 font-light leading-relaxed">
                Dedicated to providing an emotional connection to the African wilderness.
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/10 rounded-3xl flex items-center justify-center mx-auto mb-8 transition-transform group-hover:scale-110">
                <MapPinIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Local Heritage</h3>
              <p className="text-slate-500 dark:text-slate-400 font-light leading-relaxed">
                In-depth cultural roots ensuring authentic access to East Africa's secrets.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Guides;
